import {
  ContentRepresentation,
  TaxonomyAutoClassificationMode,
  TaxonomyCategory,
  TaxonomyFacet,
  TaxonomyKind,
  TaxonomyTermOrigin,
  TaxonomyTermTargetType,
  type StandardAccountLevelTaxonomyFacet,
} from '@edgebox/sync-core-data-definitions';
import {
  faBookOpen,
  faBox,
  faBrain,
  faBuildingColumns,
  faBullseye,
  faCertificate,
  faFileLines,
  faFilter,
  faFlag,
  faGlobe,
  faGraduationCap,
  faIndustry,
  faLanguage,
  faLayerGroup,
  faListCheck,
  faPuzzlePiece,
  faScaleBalanced,
  faSitemap,
  faTag,
  faTriangleExclamation,
  faUserGroup,
} from '@fortawesome/free-solid-svg-icons';
import { faBullseyeArrow } from '@fortawesome/pro-solid-svg-icons/faBullseyeArrow';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

/**
 * How a facet participates in taxonomy selection:
 * - `system`: provided org-wide by the backend (AccessScope.Public seed). Never shown in
 *   selection UIs and never created by the frontend.
 * - `required`: the product-defined baseline every project must have. Shown pre-selected
 *   and locked; created on demand if missing.
 * - `optional`: recommendable per organization type or AI suggestion; user opt-in.
 */
export type FacetScope = 'system' | 'required' | 'optional';

export interface IFacetDefaultSettings {
  name: string;
  isTimeBased: boolean;
  category: TaxonomyCategory;
  kind: TaxonomyKind;
  allowedOrigins: TaxonomyTermOrigin[];
  allowedTargets: TaxonomyTermTargetType[];
  allowAddingTerms: boolean;
  autoClassifyContent: {
    [ContentRepresentation.Page]: TaxonomyAutoClassificationMode;
    [ContentRepresentation.Entity]: TaxonomyAutoClassificationMode;
    [ContentRepresentation.File]: TaxonomyAutoClassificationMode;
  };
}

export interface IFacetMetadata {
  name: string;
  description: string;
  icon: IconDefinition;
  category: TaxonomyCategory;
  scope: FacetScope;
  /** Only present for facets the frontend may create (scope `required` or `optional`). */
  creationDefaults?: IFacetDefaultSettings;
}

const AUTO_NONE = {
  [ContentRepresentation.Page]: TaxonomyAutoClassificationMode.None,
  [ContentRepresentation.Entity]: TaxonomyAutoClassificationMode.None,
  [ContentRepresentation.File]: TaxonomyAutoClassificationMode.None,
};

const AUTO_INFERRED = {
  [ContentRepresentation.Page]: TaxonomyAutoClassificationMode.Inferred,
  [ContentRepresentation.Entity]: TaxonomyAutoClassificationMode.Inferred,
  [ContentRepresentation.File]: TaxonomyAutoClassificationMode.Inferred,
};

const AUTO_EXPLICIT = {
  [ContentRepresentation.Page]: TaxonomyAutoClassificationMode.Explicit,
  [ContentRepresentation.Entity]: TaxonomyAutoClassificationMode.Explicit,
  [ContentRepresentation.File]: TaxonomyAutoClassificationMode.Explicit,
};

const AUTO_FORMULA_PER_TERM = {
  [ContentRepresentation.Page]: TaxonomyAutoClassificationMode.FormulaPerTerm,
  [ContentRepresentation.Entity]: TaxonomyAutoClassificationMode.FormulaPerTerm,
  [ContentRepresentation.File]: TaxonomyAutoClassificationMode.FormulaPerTerm,
};

const CONTENT_AND_PROMPT_TARGETS = [TaxonomyTermTargetType.Governance_Content, TaxonomyTermTargetType.Governance_MonitoringPrompt];

function projectFacetDefaults(settings: Omit<IFacetDefaultSettings, 'allowedOrigins' | 'allowedTargets' | 'allowAddingTerms'>) {
  return {
    ...settings,
    allowedOrigins: [TaxonomyTermOrigin.User],
    allowedTargets: CONTENT_AND_PROMPT_TARGETS,
    allowAddingTerms: true,
  };
}

export const FACET_METADATA: ReadonlyMap<TaxonomyFacet, IFacetMetadata> = new Map<TaxonomyFacet, IFacetMetadata>([
  // Required project-scoped facets: pre-selected, locked, always created when missing.
  [
    TaxonomyFacet.AuthorityTopic,
    {
      name: 'Authority topic',
      description:
        'Authority topics define the core subject areas you want to measure. They power topic-level reporting and visibility trends. These are the specific topics your visitors care about.',
      icon: faBookOpen,
      category: TaxonomyCategory.Strategy,
      scope: 'required',
      creationDefaults: projectFacetDefaults({
        name: 'Authority topic',
        isTimeBased: false,
        category: TaxonomyCategory.Strategy,
        kind: TaxonomyKind.Score,
        autoClassifyContent: {
          [ContentRepresentation.Page]: TaxonomyAutoClassificationMode.Inferred,
          [ContentRepresentation.Entity]: TaxonomyAutoClassificationMode.None,
          [ContentRepresentation.File]: TaxonomyAutoClassificationMode.None,
        },
      }),
    },
  ],
  [
    TaxonomyFacet.AuthorityTopicCategory,
    {
      name: 'Authority topic category',
      description:
        'Authority topic categories group related authority topics into broader clusters. They are used to roll up analysis and compare performance across strategic areas. Include clear parent groupings that reflect how your team plans content and measures brand visibility success.',
      icon: faSitemap,
      category: TaxonomyCategory.Strategy,
      scope: 'required',
      creationDefaults: projectFacetDefaults({
        name: 'Authority topic category',
        isTimeBased: false,
        category: TaxonomyCategory.Strategy,
        kind: TaxonomyKind.Collection,
        autoClassifyContent: AUTO_FORMULA_PER_TERM,
      }),
    },
  ],
  [
    TaxonomyFacet.Initiative,
    {
      name: 'Initiative',
      description:
        'Initiatives are the campaigns and projects your team runs over a specific period. They are used to group content and measure the impact of coordinated efforts. Include active campaigns, launches, and strategic pushes you want to track.',
      icon: faFlag,
      category: TaxonomyCategory.Operations,
      scope: 'required',
      creationDefaults: projectFacetDefaults({
        name: 'Initiative',
        isTimeBased: true,
        category: TaxonomyCategory.Operations,
        kind: TaxonomyKind.Initiative,
        autoClassifyContent: AUTO_NONE,
      }),
    },
  ],
  [
    TaxonomyFacet.Brand,
    {
      name: 'Brand',
      description:
        'Your brand(s). They are used for brand-level analysis and attribution in reports. Include primary brand names, sub-brands, and common branded references.',
      icon: faTag,
      category: TaxonomyCategory.Structure,
      scope: 'required',
      creationDefaults: projectFacetDefaults({
        name: 'Brand',
        isTimeBased: false,
        category: TaxonomyCategory.Structure,
        kind: TaxonomyKind.Dimension,
        autoClassifyContent: AUTO_EXPLICIT,
      }),
    },
  ],
  [
    TaxonomyFacet.Incident,
    {
      name: 'Incident',
      description:
        'Incidents are time-bound events that can affect your brand reputation, such as outages or negative press. They are used to correlate content and visibility changes with what happened. Include incidents you want to monitor and analyze the impact of.',
      icon: faTriangleExclamation,
      category: TaxonomyCategory.TrustAndSafety,
      scope: 'required',
      creationDefaults: projectFacetDefaults({
        name: 'Incident',
        isTimeBased: true,
        category: TaxonomyCategory.TrustAndSafety,
        kind: TaxonomyKind.Incident,
        autoClassifyContent: AUTO_NONE,
      }),
    },
  ],
  [
    TaxonomyFacet.Persona,
    {
      name: 'Persona',
      description:
        'The audience profiles your content is intended for. They are used to track coverage and performance by audience type. Include your priority personas and decision-maker roles.',
      icon: faUserGroup,
      category: TaxonomyCategory.Structure,
      scope: 'required',
      creationDefaults: projectFacetDefaults({
        name: 'Persona',
        isTimeBased: false,
        category: TaxonomyCategory.Structure,
        kind: TaxonomyKind.Dimension,
        autoClassifyContent: AUTO_INFERRED,
      }),
    },
  ],
  [
    TaxonomyFacet.FunnelStage,
    {
      name: 'Funnel stage',
      description:
        'Funnel stages capture where content fits in the buyer journey. They are used to assess balance and performance across awareness, consideration, and decision phases. Include the stage labels your team uses in planning.',
      icon: faFilter,
      category: TaxonomyCategory.Structure,
      scope: 'required',
      creationDefaults: projectFacetDefaults({
        name: 'Funnel stage',
        isTimeBased: false,
        category: TaxonomyCategory.Structure,
        kind: TaxonomyKind.Dimension,
        autoClassifyContent: AUTO_INFERRED,
      }),
    },
  ],
  [
    TaxonomyFacet.Intent,
    {
      name: 'Intent',
      description:
        'Intent terms describe what users are trying to achieve when searching or engaging. They are used to map content to user needs and optimize coverage. Include the main informational, comparative, and action-oriented intents you target.',
      icon: faBrain,
      category: TaxonomyCategory.Structure,
      scope: 'required',
      creationDefaults: projectFacetDefaults({
        name: 'Intent',
        isTimeBased: false,
        category: TaxonomyCategory.Structure,
        kind: TaxonomyKind.Dimension,
        autoClassifyContent: AUTO_INFERRED,
      }),
    },
  ],

  // Optional project-scoped facets: recommended by organization type or AI suggestion.
  [
    TaxonomyFacet.Product,
    {
      name: 'Product',
      description:
        'Product terms represent the solutions or offerings you publish content about. They are used to evaluate visibility and coverage by product line. Include official product names and meaningful product families.',
      icon: faBox,
      category: TaxonomyCategory.Structure,
      scope: 'optional',
      creationDefaults: projectFacetDefaults({
        name: 'Product',
        isTimeBased: false,
        category: TaxonomyCategory.Structure,
        kind: TaxonomyKind.Dimension,
        autoClassifyContent: AUTO_EXPLICIT,
      }),
    },
  ],
  [
    TaxonomyFacet.ProductFeature,
    {
      name: 'Product feature',
      description:
        'Product features are the specific capabilities within your products that customers evaluate. They are used to measure how well individual features are covered and discovered. Include the feature names your audience searches for and compares.',
      icon: faPuzzlePiece,
      category: TaxonomyCategory.Structure,
      scope: 'optional',
      creationDefaults: projectFacetDefaults({
        name: 'Product feature',
        isTimeBased: false,
        category: TaxonomyCategory.Structure,
        kind: TaxonomyKind.Dimension,
        autoClassifyContent: AUTO_EXPLICIT,
      }),
    },
  ],
  [
    TaxonomyFacet.CompetitorProduct,
    {
      name: 'Competitor product',
      description:
        'Competitor products are the rival offerings your audience compares you against. They are used for competitive benchmarking and share-of-voice analysis at the product level. Include the competing products most often evaluated as alternatives.',
      icon: faBullseye,
      category: TaxonomyCategory.Structure,
      scope: 'optional',
      creationDefaults: projectFacetDefaults({
        name: 'Competitor product',
        isTimeBased: false,
        category: TaxonomyCategory.Structure,
        kind: TaxonomyKind.Dimension,
        autoClassifyContent: AUTO_EXPLICIT,
      }),
    },
  ],
  [
    TaxonomyFacet.Market,
    {
      name: 'Market',
      description:
        'Markets define the segments or regions you operate in. They are used to compare performance across business contexts. Include the key markets, verticals, or geographies you report on.',
      icon: faGlobe,
      category: TaxonomyCategory.Structure,
      scope: 'optional',
      creationDefaults: projectFacetDefaults({
        name: 'Market',
        isTimeBased: false,
        category: TaxonomyCategory.Structure,
        kind: TaxonomyKind.Collection,
        autoClassifyContent: AUTO_NONE,
      }),
    },
  ],
  [
    TaxonomyFacet.TargetIndustry,
    {
      name: 'Target industry',
      description:
        'Target industries are the sectors your organization sells into or serves. They are used to segment coverage and visibility by industry vertical. Include the industries your go-to-market strategy prioritizes.',
      icon: faIndustry,
      category: TaxonomyCategory.Structure,
      scope: 'optional',
      creationDefaults: projectFacetDefaults({
        name: 'Target industry',
        isTimeBased: false,
        category: TaxonomyCategory.Structure,
        kind: TaxonomyKind.Dimension,
        autoClassifyContent: AUTO_INFERRED,
      }),
    },
  ],
  [
    TaxonomyFacet.AcademicUnit,
    {
      name: 'Academic unit',
      description:
        'Academic units are the schools, colleges, and departments that make up your institution. They are used to compare content coverage and visibility across organizational units. Include the official names of your schools, faculties, and departments.',
      icon: faBuildingColumns,
      category: TaxonomyCategory.Structure,
      scope: 'optional',
      creationDefaults: projectFacetDefaults({
        name: 'Academic unit',
        isTimeBased: false,
        category: TaxonomyCategory.Structure,
        kind: TaxonomyKind.Dimension,
        autoClassifyContent: AUTO_INFERRED,
      }),
    },
  ],
  [
    TaxonomyFacet.Program,
    {
      name: 'Program',
      description:
        'Programs are the degree, certificate, and course offerings your institution promotes. They are used to measure visibility and coverage per program. Include official program names as prospective students search for them.',
      icon: faGraduationCap,
      category: TaxonomyCategory.Structure,
      scope: 'optional',
      creationDefaults: projectFacetDefaults({
        name: 'Program',
        isTimeBased: false,
        category: TaxonomyCategory.Structure,
        kind: TaxonomyKind.Dimension,
        autoClassifyContent: AUTO_EXPLICIT,
      }),
    },
  ],
  [
    TaxonomyFacet.CompetitorProgram,
    {
      name: 'Competitor program',
      description:
        'Competitor programs are the offerings at other institutions that prospective students consider instead of yours. They are used for competitive benchmarking per program area. Include the rival programs your applicants most often compare against.',
      icon: faBullseye,
      category: TaxonomyCategory.Structure,
      scope: 'optional',
      creationDefaults: projectFacetDefaults({
        name: 'Competitor program',
        isTimeBased: false,
        category: TaxonomyCategory.Structure,
        kind: TaxonomyKind.Dimension,
        autoClassifyContent: AUTO_EXPLICIT,
      }),
    },
  ],
  [
    TaxonomyFacet.Credential,
    {
      name: 'Credential',
      description:
        'Credentials are the degrees, certificates, and qualifications your institution awards. They are used to analyze content by the outcome a learner earns. Include credential types such as bachelor’s, master’s, certificates, and micro-credentials.',
      icon: faCertificate,
      category: TaxonomyCategory.Structure,
      scope: 'optional',
      creationDefaults: projectFacetDefaults({
        name: 'Credential',
        isTimeBased: false,
        category: TaxonomyCategory.Structure,
        kind: TaxonomyKind.Dimension,
        autoClassifyContent: AUTO_EXPLICIT,
      }),
    },
  ],
  [
    TaxonomyFacet.AcademicLevel,
    {
      name: 'Academic level',
      description:
        'Academic levels distinguish the stages of study you offer, such as undergraduate and graduate. They are used to segment reporting by the audience’s stage of education. Include the levels your institution serves.',
      icon: faLayerGroup,
      category: TaxonomyCategory.Structure,
      scope: 'optional',
      creationDefaults: projectFacetDefaults({
        name: 'Academic level',
        isTimeBased: false,
        category: TaxonomyCategory.Structure,
        kind: TaxonomyKind.Dimension,
        autoClassifyContent: AUTO_EXPLICIT,
      }),
    },
  ],
  [
    TaxonomyFacet.ComplianceStandard,
    {
      name: 'Compliance standard',
      description:
        'Compliance standards are the regulations and certifications relevant to your industry. They are used to track which content addresses which requirements. Include the standards your customers and auditors ask about.',
      icon: faScaleBalanced,
      category: TaxonomyCategory.Structure,
      scope: 'optional',
      creationDefaults: projectFacetDefaults({
        name: 'Compliance standard',
        isTimeBased: false,
        category: TaxonomyCategory.Structure,
        kind: TaxonomyKind.Dimension,
        autoClassifyContent: AUTO_NONE,
      }),
    },
  ],

  // Attribution v2 aspect facets: closed lists of comparison criteria the attribution
  // engine measures answers against. `organization-attribute` is onboarding-required;
  // `product-attribute` locks in once products are tracked (PRODUCT_ATTRIBUTE_REQUIRED_WHEN).
  [
    TaxonomyFacet.OrganizationAttribute,
    {
      name: 'Organization attribute',
      description:
        'Organization attributes are the organization-level criteria answers use to compare you, such as “trusted by enterprises” or “backed by a strong ecosystem”. They are used to standardize comparison aspects and measure the explicit recommendation. Include the decision criteria buyers weigh at the company level.',
      icon: faScaleBalanced,
      category: TaxonomyCategory.Strategy,
      scope: 'required',
      creationDefaults: projectFacetDefaults({
        name: 'Organization attribute',
        isTimeBased: false,
        category: TaxonomyCategory.Strategy,
        kind: TaxonomyKind.Dimension,
        autoClassifyContent: AUTO_NONE,
      }),
    },
  ],
  [
    TaxonomyFacet.ProductAttribute,
    {
      name: 'Product attribute',
      description:
        'Product attributes are the product-level decision criteria answers compare, including provider-derived ones such as “backed by the vendor’s enterprise ecosystem”. They are used to standardize product comparison aspects. Include the qualities buyers weigh when choosing between products.',
      icon: faListCheck,
      category: TaxonomyCategory.Strategy,
      scope: 'optional',
      creationDefaults: projectFacetDefaults({
        name: 'Product attribute',
        isTimeBased: false,
        category: TaxonomyCategory.Strategy,
        kind: TaxonomyKind.Dimension,
        autoClassifyContent: AUTO_NONE,
      }),
    },
  ],

  // System facets: seeded org-wide by the backend (AccessScope.Public).
  // Never shown in selection UIs and never created by the frontend. Descriptions are
  // still used where their terms are managed (e.g. onboarding steps 4/5, settings).
  [
    TaxonomyFacet.Competitor,
    {
      name: 'Competitor',
      description:
        'Competitors identify external brands you want to benchmark against. They are used for competitive reporting and share-of-voice comparisons. Include your direct competitors and major alternatives your audience evaluates.',
      icon: faBullseye,
      category: TaxonomyCategory.Strategy,
      scope: 'system',
    },
  ],
  [
    TaxonomyFacet.AuthoritySource,
    {
      name: 'Authority source',
      description:
        'Authority sources define the channels or sources influencing your topic space. They are used to understand where authority signals come from in your monitoring. Include relevant publishers, platforms, and source domains.',
      icon: faGlobe,
      category: TaxonomyCategory.Strategy,
      scope: 'system',
    },
  ],
  [
    TaxonomyFacet.ContentFeatureSuggestion,
    {
      name: 'Content feature suggestion',
      description:
        'Content feature suggestions are improvement ideas detected for your content. They are used to queue and track optimization opportunities. Terms are managed automatically by the platform.',
      icon: faFileLines,
      category: TaxonomyCategory.Content,
      scope: 'system',
    },
  ],
  [
    TaxonomyFacet.ContentFeature,
    {
      name: 'Content feature',
      description:
        'Content features describe the structural elements present in your content, such as FAQs or tables. They are used to analyze which formats drive visibility. Terms are managed automatically by the platform.',
      icon: faFileLines,
      category: TaxonomyCategory.Content,
      scope: 'system',
    },
  ],
  [
    TaxonomyFacet.ContentFeatureQuality,
    {
      name: 'Content feature quality',
      description:
        'Content feature quality captures how well each content feature is implemented. It is used to prioritize improvements across your content. Terms are managed automatically by the platform.',
      icon: faFileLines,
      category: TaxonomyCategory.Content,
      scope: 'system',
    },
  ],
  [
    TaxonomyFacet.Site,
    {
      name: 'Site',
      description:
        'Sites represent the web properties connected to your organization. They are used to segment analysis per site. Terms are managed automatically by the platform.',
      icon: faSitemap,
      category: TaxonomyCategory.System,
      scope: 'system',
    },
  ],
  [
    TaxonomyFacet.Locale,
    {
      name: 'Locale',
      description:
        'Locales are used to segment insights by audience language and location and allow you to fine-tune prompts and reporting. Include all active language-region combinations you publish for.',
      icon: faLanguage,
      category: TaxonomyCategory.System,
      scope: 'system',
    },
  ],
  [
    TaxonomyFacet.ContentType,
    {
      name: 'Content type',
      description:
        'Content types classify the format of each page, such as blog post, product page, or documentation. They are used to compare performance across formats. Terms are managed automatically by the platform.',
      icon: faFileLines,
      category: TaxonomyCategory.Content,
      scope: 'system',
    },
  ],
  [
    TaxonomyFacet.PageType,
    {
      name: 'Page type',
      description:
        'Page types classify pages by their structural role on your site. They are used to group analysis by page template and purpose. Terms are managed automatically by the platform.',
      icon: faFileLines,
      category: TaxonomyCategory.Content,
      scope: 'system',
    },
  ],
  [
    TaxonomyFacet.Engine,
    {
      name: 'Engine',
      description:
        'Engines are the AI answer and search engines your visibility is measured on. They are used to segment monitoring results per engine. Terms are managed automatically by the platform.',
      icon: faBrain,
      category: TaxonomyCategory.System,
      scope: 'system',
    },
  ],
  [
    TaxonomyFacet.IssueType,
    {
      name: 'Issue type',
      description:
        'Issue types classify the problems detected on your content. They are used to group and prioritize remediation work. Terms are managed automatically by the platform.',
      icon: faTriangleExclamation,
      category: TaxonomyCategory.TrustAndSafety,
      scope: 'system',
    },
  ],
  [
    TaxonomyFacet.IssueCategory,
    {
      name: 'Issue category',
      description:
        'Issue categories group related issue types into broader clusters. They are used to roll up issue reporting. Terms are managed automatically by the platform.',
      icon: faTriangleExclamation,
      category: TaxonomyCategory.TrustAndSafety,
      scope: 'system',
    },
  ],
  [
    TaxonomyFacet.IssueDetection,
    {
      name: 'Issue detection',
      description:
        'Issue detections record how and where issues were identified. They are used to trace findings back to their detection source. Terms are managed automatically by the platform.',
      icon: faTriangleExclamation,
      category: TaxonomyCategory.TrustAndSafety,
      scope: 'system',
    },
  ],
  // The taxonomy itself is account-level and comes from the backend like the other system facets,
  // so the frontend never creates it. Its terms are the operator's, though: each purpose is created
  // and typed here, and the type decides which KPIs the analytics show for its prompts.
  [
    TaxonomyFacet.MonitoringPromptPurpose,
    {
      name: 'Prompt purpose',
      description:
        'Prompt purposes say what each monitoring prompt is for. Every purpose carries a type — Discovery, Comparison, Brand probe, Competitor probe or Custom — and the type decides which KPIs AI Visibility shows for its prompts, because a question that already names your brand has fixed your visibility in advance. Create one purpose per kind of question you monitor and give each prompt one.',
      icon: faBullseyeArrow,
      category: TaxonomyCategory.Operations,
      scope: 'system',
    },
  ],
]);

/**
 * Facets that make `product-attribute` a required aspect facet when any of them is in use:
 * product decision criteria are only meaningful once products are tracked.
 */
export const PRODUCT_ATTRIBUTE_REQUIRED_WHEN: readonly TaxonomyFacet[] = [TaxonomyFacet.Product, TaxonomyFacet.CompetitorProduct];

/** The facets every project must have; pre-selected and locked in selection UIs. */
export const REQUIRED_PROJECT_FACETS: readonly StandardAccountLevelTaxonomyFacet[] = Array.from(FACET_METADATA.entries())
  .filter(([, metadata]) => metadata.scope === 'required')
  .map(([facet]) => facet as StandardAccountLevelTaxonomyFacet);

/** The facets users may add per project (recommended or via "Show all"). */
export const OPTIONAL_PROJECT_FACETS: readonly StandardAccountLevelTaxonomyFacet[] = Array.from(FACET_METADATA.entries())
  .filter(([, metadata]) => metadata.scope === 'optional')
  .map(([facet]) => facet as StandardAccountLevelTaxonomyFacet);

export function facetMetadata(facet: TaxonomyFacet | undefined): IFacetMetadata | undefined {
  return facet ? FACET_METADATA.get(facet) : undefined;
}

export function facetScope(facet: TaxonomyFacet | undefined): FacetScope | undefined {
  return facetMetadata(facet)?.scope;
}

export function facetDisplayName(facet: TaxonomyFacet): string {
  return facetMetadata(facet)?.name || String(facet);
}

export function facetIcon(facet: TaxonomyFacet | undefined): IconDefinition {
  return facetMetadata(facet)?.icon || faTag;
}

export function facetDescription(facet: TaxonomyFacet | undefined, fallbackName?: string): string {
  const metadata = facetMetadata(facet);
  if (metadata?.description) {
    return metadata.description;
  }

  const name = fallbackName || metadata?.name || String(facet || 'This taxonomy');
  return `${name} groups related terms for reporting and filtering. It is used to organize analysis and keep classification consistent. Include terms your team actively tracks and uses in decisions.`;
}

export function facetCreationDefaults(facet: TaxonomyFacet | undefined): IFacetDefaultSettings | undefined {
  return facetMetadata(facet)?.creationDefaults;
}
